import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';

const NotificationContext = createContext(null);

export const useNotifications = () => {
    const context = useContext(NotificationContext);
    if (!context) {
        throw new Error('useNotifications must be used within a NotificationProvider');
    }
    return context;
};

export const NotificationProvider = ({ children }) => {
    const { authAxios, isAuthenticated } = useAuth();
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);

    // Poll for new high-risk transactions
    const checkForAlerts = useCallback(async () => {
        if (!isAuthenticated) return;
        
        try {
            // Get recent webhook transactions
            const response = await authAxios.get('/transactions?type=webhook&limit=5');
            const transactions = response.data || [];
            
            // Filter high-risk ones that we haven't notified about
            const notifiedIds = JSON.parse(localStorage.getItem('notified_tx') || '[]');
            const newHighRisk = transactions.filter(
                tx => tx.risk_level === 'high' && !notifiedIds.includes(tx.id)
            );
            
            if (newHighRisk.length > 0) {
                // Add to notifications
                const newNotifs = newHighRisk.map(tx => ({
                    id: tx.id,
                    type: 'high_risk',
                    title: '⚠️ High Risk Transaction',
                    message: `₹${tx.amount?.toLocaleString()} payment flagged as suspicious`,
                    timestamp: new Date().toISOString(),
                    read: false,
                    data: tx
                }));
                
                setNotifications(prev => [...newNotifs, ...prev]);
                setUnreadCount(prev => prev + newNotifs.length);
                
                // Mark as notified
                const updatedNotified = [...notifiedIds, ...newHighRisk.map(tx => tx.id)];
                localStorage.setItem('notified_tx', JSON.stringify(updatedNotified.slice(-100)));
                
                // Show browser notification if supported
                if ('Notification' in window && Notification.permission === 'granted') {
                    newHighRisk.forEach(tx => {
                        new Notification('⚠️ High Risk Alert', {
                            body: `₹${tx.amount?.toLocaleString()} payment needs review`,
                            icon: '/logo192.png',
                            tag: tx.id
                        });
                    });
                }
            }
        } catch (err) {
            // Silent fail
        }
    }, [authAxios, isAuthenticated]);

    // Poll every 30 seconds
    useEffect(() => {
        if (!isAuthenticated) return;
        
        checkForAlerts();
        const interval = setInterval(checkForAlerts, 30000);
        
        return () => clearInterval(interval);
    }, [isAuthenticated, checkForAlerts]);

    const markAsRead = (id) => {
        setNotifications(prev => 
            prev.map(n => n.id === id ? { ...n, read: true } : n)
        );
        setUnreadCount(prev => Math.max(0, prev - 1));
    };

    const markAllAsRead = () => {
        setNotifications(prev => prev.map(n => ({ ...n, read: true })));
        setUnreadCount(0);
    };

    const clearNotifications = () => {
        setNotifications([]);
        setUnreadCount(0);
    };

    const addNotification = (notification) => {
        setNotifications(prev => [notification, ...prev]);
        setUnreadCount(prev => prev + 1);
    };

    return (
        <NotificationContext.Provider value={{
            notifications,
            unreadCount,
            markAsRead,
            markAllAsRead,
            clearNotifications,
            addNotification,
            checkForAlerts
        }}>
            {children}
        </NotificationContext.Provider>
    );
};

export default NotificationContext;
